import { computed, ref, toValue, type MaybeRefOrGetter } from 'vue'
import { useContentAccessStore } from '@/content_access/content_access.store'
import type { AnalyticsTracker } from '@/tracking/analytics/analyticsTracker'
import { CONTENT_TYPES } from '@/util/access_visibility'
import { useAnchorPageStore } from './anchor_page.store'
import { ANCHOR_PAGE_EVENT_NAMES } from './anchor_page_event_names'

export interface SavedAnchorPrintTarget {
  savedPageUuid: string
  title: string
  sourceGoalUuid: string
}

/**
 * Opens the saved anchor page's print preview, or the limit modal when printing is blocked,
 * and records the print. Takes the page's tracker so events carry the page context.
 */
export function useSavedAnchorPrint(target: MaybeRefOrGetter<SavedAnchorPrintTarget>, analytics: AnalyticsTracker) {
  const anchorPageStore = useAnchorPageStore()
  const contentAccessStore = useContentAccessStore()

  const isPrintViewOpen = ref(false)
  const isPrintBlockedModalOpen = ref(false)
  const printPermissions = computed(() => anchorPageStore.actionPermissions.printSavedAnchor)

  function openPrintView() {
    const isBlocked = printPermissions.value.block
    analytics.sendEvent(ANCHOR_PAGE_EVENT_NAMES.CLICKED_PRINT_SAVED_PAGE, { access: !isBlocked, uuid: toValue(target).savedPageUuid })
    if (isBlocked) {
      isPrintBlockedModalOpen.value = true
      return
    }
    isPrintViewOpen.value = true
  }

  function closePrintView() {
    isPrintViewOpen.value = false
  }

  // Vue 2 sent no content uuid; the saved page's is passed so the print joins to what was printed.
  function trackPrint() {
    const { savedPageUuid, title, sourceGoalUuid } = toValue(target)
    contentAccessStore.trackPrint({
      contentType: CONTENT_TYPES.SAVED_SUPER_PAGE,
      contentTitle: title,
      contentUuid: savedPageUuid,
      otherData: { superpage_uuid: sourceGoalUuid }
    })
  }

  return { printPermissions, isPrintViewOpen, isPrintBlockedModalOpen, openPrintView, closePrintView, trackPrint }
}
